// Client Portal — BuildIQ "Document templates" editor. The Spec / Contract /
// Change-Order legal boilerplate is per-facility and lives in the price book's
// `cp` object; this edits it and saves via PUT /api/pricebook (the full book is
// loaded, only `cp` is changed, then written back). The BuildIQ app reads these
// same `cp` values when rendering the documents.
//
// NOTE: this is user-facing LEGAL copy. The placeholders below show the shipping
// defaults; a facility admin owns the wording. Nothing here is authored by us.
import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { BUILDIQ_URL, getToken } from "../lib/api";

// Faithful defaults (mirror buildiq lib/doctemplate) — shown as placeholders so
// the admin sees the current effective wording when a field is unset.
const SPEC_INTRO_DEFAULT =
  "The following specifications and materials apply to the contract between {contractor} and {client}.  Property address is {address}.  This contract is dated {date}.";
const CO_AGREEMENT_DEFAULT =
  "The above change(s) are hereby agreed to and incorporated into the original contract. All other terms and conditions remain unchanged.";
const NUDGE_FOLLOWUP_DEFAULT =
  "Hi! Just following up on the estimate for {job} — happy to answer any questions or " +
  "adjust anything. Whenever you're ready, we'd love to get you on the schedule.{company}";
const NUDGE_STARTS_DEFAULT =
  "Heads up — {trade} starts {when} at {address}. We'll keep things moving and keep you " +
  "posted. Any questions, just reply here.{company}";
const NUDGE_PAYMENT_DEFAULT =
  "Hi! A friendly reminder — the {draw} of {amount} for {job} was billed on {date}. " +
  "Whenever it's convenient, we'd appreciate getting that squared away. Thank you!{company}";
const CLIENT_UPDATE_INTRO_DEFAULT = "Hi {name}, here is this week's update on {job}.";
const REVIEW_MSG_DEFAULT =
  "Hi {name}, thank you for letting {company} take care of your project! If you were " +
  "happy with the work, it would mean the world if you left us a review: {link}";
const SCHED_ROWS = [
  ["grand", "The estimate including tax is:"],
  ["d0", "The deposit due at signing is:"],
  ["d1", "Due at demo completion"],
  ["d2", "Due at concrete pour"],
  ["d3", "Due at framing completion"],
  ["d4", "Due at rough in completion"],
  ["d5", "Due at dry in completion"],
  ["d6", "Due at interior paint completion"],
  ["d7", "Due at countertop install"],
  ["d8", "Balance due upon completion"],
];
const TERM_KEYS = ["22", "23", "24", "25", "26", "27", "28", "29", "30", "32", "33", "34"];

const taCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-y";
const inCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none";
const lblCls = "block text-xs font-medium text-slate-500 mb-1";

// path helpers into the working cp copy (e.g. ["sched_labels","d2"])
const getPath = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
const setPath = (obj, path, val) => {
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = { ...(cur[path[i]] || {}) };
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = val;
  return next;
};

export const BuildIQDocTemplates = ({ fid }) => {
  const [book, setBook] = useState(null); // full price book (kept so PUT preserves pricing)
  const [cp, setCp] = useState(null); // working copy of book.cp
  const [status, setStatus] = useState("loading"); // loading|ok|error
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const api = async (path, options = {}) => {
    const res = await fetch(`${BUILDIQ_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || `HTTP ${res.status}`);
    return data;
  };

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    api(`/api/pricebook?facility_id=${fid}`)
      .then((d) => {
        if (!alive) return;
        const b = d.price_book || {};
        setBook(b);
        setCp({ ...(b.cp || {}) });
        setStatus("ok");
      })
      .catch((e) => { if (alive) { setStatus("error"); setMsg({ type: "err", text: e.message }); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fid]);

  const field = (path) => getPath(cp, path) || "";
  const edit = (path, val) => setCp((c) => setPath(c, path, val));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const d = await api("/api/pricebook", {
        method: "PUT",
        body: JSON.stringify({ facility_id: fid, price_book: { ...book, cp } }),
      });
      setMsg({ type: "ok", text: "Document templates saved." });
      if (d && d.saved) setBook((b) => ({ ...b, cp }));
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading")
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading document templates…
      </div>
    );
  if (status === "error")
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-red-600">
        Couldn’t load the price book{msg ? ` — ${msg.text}` : ""}.
      </div>
    );

  const Textarea = ({ label, path, rows = 3, placeholder, hint }) => (
    <div>
      <label className={lblCls}>{label}</label>
      <textarea className={taCls} rows={rows} value={field(path)} placeholder={placeholder || ""} onChange={(e) => edit(path, e.target.value)} />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
      <div>
        <h3 className="font-semibold text-slate-800">Document templates</h3>
        <p className="text-xs text-slate-400">
          The wording on your Spec, Contract, Change Order and review requests. Leave a field blank to use the built-in default (shown as the placeholder). Contract terms left blank simply don’t appear on the document.
        </p>
      </div>

      {msg && <p className={`text-sm ${msg.type === "ok" ? "text-green-600" : "text-red-600"}`}>{msg.text}</p>}

      {/* Spec */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Spec</h4>
        <Textarea label="Opening sentence" path={["spec_intro"]} placeholder={SPEC_INTRO_DEFAULT} hint="Placeholders filled per job: {contractor} · {client} · {address} · {date}" />
      </section>

      {/* Contract */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Contract</h4>
        <Textarea label="Opening paragraph 1" path={["6"]} />
        <Textarea label="Opening paragraph 2" path={["7"]} />
        <Textarea label="Pre-signature paragraph" path={["18"]} />
      </section>

      {/* Payment schedule labels */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Contract — payment schedule labels</h4>
        <p className="text-[11px] text-slate-400 -mt-1">The milestone wording beside each amount. Amounts are entered per job.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {SCHED_ROWS.map(([k, def]) => (
            <div key={k}>
              <label className={lblCls}>{k === "grand" ? "Estimate total" : k.toUpperCase()}</label>
              <input className={inCls} value={field(["sched_labels", k])} placeholder={def} onChange={(e) => edit(["sched_labels", k], e.target.value)} />
            </div>
          ))}
        </div>
      </section>

      {/* Terms */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Contract — terms</h4>
        {TERM_KEYS.map((k, i) => (
          <Textarea key={k} label={`Term ${i + 1}`} path={[k]} rows={2} />
        ))}
        <Textarea label="Additional term 1" path={["36"]} rows={2} />
        <Textarea label="Additional term 2" path={["37"]} rows={2} />
      </section>

      {/* Change Order */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Change Order</h4>
        <Textarea label="Agreement paragraph" path={["co_agreement"]} placeholder={CO_AGREEMENT_DEFAULT} />
      </section>

      {/* Reviews (BuildIQ N-3) — the message a finished client receives, and
          where a happy one is sent. The link has NO built-in default on purpose:
          sending your clients to somebody else's review page is worse than
          sending them nowhere, so a blank link simply removes that sentence
          from the request. */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Reviews</h4>
        <div>
          <label className={lblCls}>Review link</label>
          <input
            className={inCls}
            value={field(["review_url"])}
            placeholder="https://your-site.com/reviews"
            onChange={(e) => edit(["review_url"], e.target.value)}
          />
          <p className="text-[11px] text-slate-400 mt-1">
            Where a happy client should go to say so. Left blank, the request still sends — it just says thank you without a link.
          </p>
        </div>
        <Textarea
          label="Request message"
          path={["review_msg"]}
          placeholder={REVIEW_MSG_DEFAULT}
          hint="Placeholders filled per job: {name} is the client's first name · {company} is you · {link} is the review link above"
        />
      </section>

      {/* Nudges (BuildIQ S1-4b) — the three follow-ups the app drafts for you.
          None of them sends itself; each opens a message you press send on. */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Follow-up messages</h4>
        <p className="text-[11px] text-slate-400 -mt-1">
          Drafted for you when an estimate goes quiet, when work is about to start, or when a billed draw is still open. Nothing sends automatically — you press send.
        </p>
        <Textarea label="Estimate follow-up" path={["nudge_followup"]} placeholder={NUDGE_FOLLOWUP_DEFAULT} rows={3}
          hint="Placeholders: {job} · {company} (your name, with the dash, or nothing if unset)" />
        <Textarea label="Work starting notice" path={["nudge_starts"]} placeholder={NUDGE_STARTS_DEFAULT} rows={3}
          hint="Placeholders: {trade} · {when} (“tomorrow” or a date) · {address} · {company}" />
        <Textarea label="Payment reminder" path={["nudge_payment"]} placeholder={NUDGE_PAYMENT_DEFAULT} rows={3}
          hint="Placeholders: {draw} · {amount} · {job} · {date} · {company}" />
      </section>

      {/* Client updates (BuildIQ N-4) — only the OPENING is wording. Everything
          under it is assembled from the site diary and the schedule, and the
          office edits the whole draft before it goes anywhere. */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Client updates</h4>
        <Textarea
          label="Opening line"
          path={["client_update_intro"]}
          placeholder={CLIENT_UPDATE_INTRO_DEFAULT}
          rows={2}
          hint="The rest of the update is built from the site diary and the schedule. Placeholders: {name} is the client's first name · {job} is the job name"
        />
      </section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-slate-900 text-sm font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save document templates"}
        </button>
      </div>
    </div>
  );
};
